import { FormMethod, Link, useSearchParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import annotatedSampleInvoice from '@/assets/annotated_sample_invoice.png';
import ButtonRow from '@/components/ButtonRow';
import FlashMessage from '@/components/FlashMessage';
import FormInput from '@/components/FormInput';
import FormInputSeparator from '@/components/FormInputSeparator';
import SubmitButton from '@/components/SubmitButton';
import TwoColumnCard from '@/components/TwoColumnCard';
import TwoColumnForm from '@/components/TwoColumnForm';
import AuthActionsCard from '@/features/payment/AuthActionsCard';
import useScrollIntoViewOnMobile from '@/hooks/useScrollIntoViewOnMobile';

// AIDEV-NOTE: Blue circle badge to match annotated invoice numbering
const NumberBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: #015ba8;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
  margin-right: 0.5rem;
`;

export interface FindInvoiceFormProps {
  readonly title: string;
  readonly subtitle: string;
  readonly method?: FormMethod;
  readonly actionLabel: string;
  readonly cancelRoute?: string;
  readonly showAuthActions?: boolean;
}

/**
 * This component serves as the basis for both the "Add Location" page and the first
 * step of the "Pay Invoice" page where the user needs to look up their invoice.
 */
export default function FindInvoiceForm({
  title,
  subtitle,
  method,
  actionLabel,
  cancelRoute,
  showAuthActions,
}: FindInvoiceFormProps) {
  return (
    <TwoColumnForm
      title={title}
      subtitle={subtitle}
      method={method}
      leftColumn={
        <EnterInvoiceDetailsCard
          actionLabel={actionLabel}
          cancelRoute={cancelRoute}
        />
      }
      rightColumn={
        <>
          <AnnotatedInvoiceCard />
          {showAuthActions && <AuthActionsCard />}
        </>
      }
    />
  );
}

interface EnterInvoiceDetailsCardProps {
  readonly actionLabel: string;
  readonly cancelRoute?: string;
}

function EnterInvoiceDetailsCard({
  actionLabel,
  cancelRoute,
}: EnterInvoiceDetailsCardProps) {
  const [searchParams] = useSearchParams();

  // AIDEV-NOTE: On mobile this card starts below the fold, hiding the annotated
  // sample invoice below it entirely. See useScrollIntoViewOnMobile.
  const { cardRef, alertRef } = useScrollIntoViewOnMobile();

  return (
    <TwoColumnCard title="Enter Invoice Details" innerRef={cardRef}>
      <FlashMessage innerRef={alertRef} />

      <h5 className="mb-3">
        Please enter your invoice number as shown on your invoice:
      </h5>
      <div className="d-flex">
        <FormInput
          name="franchiseNumber"
          label={
            <>
              <NumberBadge aria-hidden="true">1</NumberBadge>
              Franchise #
            </>
          }
          required
          defaultValue={searchParams.get('franchiseNumber') ?? ''}
        />
        <FormInputSeparator>-</FormInputSeparator>
        <FormInput
          name="workOrderNumber"
          label={
            <>
              <NumberBadge aria-hidden="true">2</NumberBadge>
              Work Order #
            </>
          }
          required
          defaultValue={searchParams.get('workOrderNumber') ?? ''}
        />
      </div>

      <div className="d-flex">
        <FormInput
          name="zipCode"
          label={
            <>
              <NumberBadge aria-hidden="true">3</NumberBadge>
              Zip Code
            </>
          }
          required
          defaultValue={searchParams.get('zipCode') ?? ''}
        />
      </div>

      <ButtonRow className="mt-4">
        {cancelRoute && (
          <Button tag={Link} to={cancelRoute} size="lg" color="secondary">
            Cancel
          </Button>
        )}
        <SubmitButton size="lg" color="primary" type="submit">
          {actionLabel}
        </SubmitButton>
      </ButtonRow>
    </TwoColumnCard>
  );
}

function AnnotatedInvoiceCard() {
  return (
    <TwoColumnCard title="Where do I find these details?" padded={false}>
      {/* AIDEV-NOTE: width/height reserve the image's space before it loads, so the
          page reaches its full height in time for useScrollIntoViewOnMobile. */}
      <img
        src={annotatedSampleInvoice}
        alt="Annotated sample invoice"
        className="img-fluid"
        width={2458}
        height={1591}
      />
    </TwoColumnCard>
  );
}
